import React, { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import SectionHeader from "@cayuse/shared-react.components.section-header";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import IconButton from "@cayuse/shared-react.components.icon-button";

import Table from "components/Table";
import { useDictionary } from "hooks";
import {
	addEmptyElement,
	findAll,
	resetChanges,
	saveChanged,
} from "store/actions/researchRoles";
import { researchRolesSelectors } from "store/selectors";

import { RolesTable } from "./RolesTable";
import styles from "./styles.css";

const MAX_ITEMS = 100;

export const ResearchTeamRolesView = () => {
	const dictionary = useDictionary("manageResearchTeamRoles");
	const isLoading = useSelector(researchRolesSelectors.isFetching);
	const isChanged = useSelector(researchRolesSelectors.isChanged);
	const { changed } = useSelector(researchRolesSelectors.content);

	const [ showCancelEditingAlert, setShowCancelEditingAlert ] = useState(false);
	const [ isEditMode, setIsEditMode ] = useState(false);
	const [ isUpdating, setIsUpdating ] = useState(false);

	useEffect(() => {
		findAll();
	}, []);

	const onSave = async () => {
		setIsUpdating(true);
		const saved = await saveChanged();
		if (saved) {
			setIsEditMode(false);
		}
		setIsUpdating(false);
	};

	const onCancel = () => {
		if (isChanged) {
			setShowCancelEditingAlert(true);
		} else {
			setIsEditMode(false);
		}
	};

	const onDiscardChanges = () => {
		resetChanges();
		setIsEditMode(false);
		setIsUpdating(false);
		setShowCancelEditingAlert(false);
	};

	const actionsDisabled = isLoading || isUpdating;
	const hasErrors = changed.some(it => it.error);
	const isSubmitDisabled = isUpdating || !isChanged || hasErrors;

	return (
		<>
			<SectionHeader header={dictionary.header} />
			<div className={styles.container}>
				<BasePadding>
					<Table isLoading={isLoading} sortable captionText={dictionary.subHeader} className={styles.researchTeamRolesViewTable}>
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>{dictionary.subHeader}</Table.HeaderCell>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							<Table.Row>
								<Table.Cell>
									<div className={styles.description}>
										<p className={styles.descriptionTitle}>{dictionary.tableDescription}</p>
										<div className={styles.actions}>
											<Button
												secondary
												disabled={!isEditMode || actionsDisabled}
												onClick={onCancel}
												data-testid="cancel-button"
											>
												{dictionary.actions.cancel}
											</Button>
											{isEditMode ? (
												<Button
													onClick={onSave}
													disabled={isSubmitDisabled}
													busy={isUpdating}
													data-testid="save-button"
												>
													{dictionary.actions.save}
												</Button>
											) : (
												<IconButton
													icon="pencil-alt"
													labelPosition="left"
													disabled={actionsDisabled}
													onClick={() => setIsEditMode(prev => !prev)}
												>
													<span data-testid="edit-button">
														{dictionary.actions.edit}
													</span>
												</IconButton>
											)}
										</div>
									</div>
									<DndProvider backend={HTML5Backend}>
										<RolesTable
											isEditMode={isEditMode}
											actionsDisabled={actionsDisabled}
										/>
									</DndProvider>
								</Table.Cell>
							</Table.Row>
							<Table.Row>
								<Table.Cell>
									<IconButton
										icon="plus"
										labelPosition="left"
										disabled={actionsDisabled || !isEditMode || MAX_ITEMS === changed.length}
										onClick={addEmptyElement}
									>
										{dictionary.actions.addNew}
									</IconButton>
								</Table.Cell>
							</Table.Row>
						</Table.Body>
					</Table>
				</BasePadding>
			</div>
			<AlertDialog
				title={dictionary.cancelEditModal.header}
				description={dictionary.cancelEditModal.message}
				isOpen={showCancelEditingAlert}
				onConfirm={onDiscardChanges}
				confirmText={dictionary.cancelEditModal.discardChanges}
				onCancel={() => setShowCancelEditingAlert(false)}
			/>
		</>
	);
};

export default ResearchTeamRolesView;
